import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Loader2, Search, ExternalLink, DollarSign, Calendar, CreditCard, User, Mail } from 'lucide-react';

interface SubscriptionData {
  user_id: string;
  email: string;
  display_name: string;
  stripe_customer_id?: string;
  subscription_status?: string;
  product_id?: string;
  price_id?: string;
  current_period_end?: string;
  monthly_credits: number;
  purchased_credits: number;
  is_trial_active: boolean;
  trial_end_date: string | null;
}

const SubscriptionOverview = () => {
  const [subscriptions, setSubscriptions] = useState<SubscriptionData[]>([]);
  const [filteredSubs, setFilteredSubs] = useState<SubscriptionData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchSubscriptionData();
  }, []);

  useEffect(() => {
    if (searchTerm.trim()) {
      const filtered = subscriptions.filter(
        (sub) =>
          sub.display_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          sub.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredSubs(filtered);
    } else {
      setFilteredSubs(subscriptions);
    }
  }, [searchTerm, subscriptions]);

  const fetchSubscriptionData = async () => {
    try {
      setIsLoading(true);

      // Fetch all users with their email from auth.users
      const { data: { users }, error: usersError } = await supabase.auth.admin.listUsers();
      if (usersError) throw usersError;

      // Fetch user credits
      const { data: creditsData, error: creditsError } = await supabase
        .from('user_credits')
        .select('*');
      if (creditsError) throw creditsError;

      // Fetch profiles
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('user_id, display_name');
      if (profilesError) throw profilesError;

      // Fetch user subscriptions (Stripe data)
      const { data: subsData, error: subsError } = await supabase
        .from('user_subscriptions')
        .select('*');
      if (subsError) throw subsError;

      // Combine data
      const combined = users.map((user) => {
        const credits = creditsData?.find((c) => c.user_id === user.id);
        const profile = profilesData?.find((p) => p.user_id === user.id);
        const subscription = subsData?.find((s) => s.user_id === user.id);

        return {
          user_id: user.id,
          email: user.email || 'No email',
          display_name: profile?.display_name || 'Unnamed User',
          stripe_customer_id: subscription?.stripe_customer_id,
          subscription_status: subscription?.subscription_status,
          product_id: subscription?.product_id,
          price_id: subscription?.price_id,
          current_period_end: subscription?.current_period_end,
          monthly_credits: credits?.monthly_credits || 0,
          purchased_credits: credits?.purchased_credits || 0,
          is_trial_active: credits?.is_trial_active || false,
          trial_end_date: credits?.trial_end_date,
        };
      });

      // Sort by subscription status (active first)
      combined.sort((a, b) => {
        if (a.subscription_status === 'active' && b.subscription_status !== 'active') return -1;
        if (a.subscription_status !== 'active' && b.subscription_status === 'active') return 1;
        return 0;
      });

      setSubscriptions(combined);
      setFilteredSubs(combined);
    } catch (error) {
      console.error('Error fetching subscription data:', error);
      toast.error('Failed to load subscription data');
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusBadge = (status?: string, isTrial?: boolean) => {
    if (isTrial) {
      return <Badge variant="outline" className="bg-blue-500/10 text-blue-600">Trial</Badge>;
    }
    
    switch (status) {
      case 'active':
        return <Badge variant="default">Active</Badge>;
      case 'trialing':
        return <Badge variant="outline" className="bg-blue-500/10 text-blue-600">Trialing</Badge>;
      case 'canceled':
        return <Badge variant="destructive">Cancelled</Badge>;
      case 'past_due':
        return <Badge variant="outline" className="bg-yellow-500/10 text-yellow-600">Past Due</Badge>;
      default:
        return <Badge variant="secondary">Free</Badge>;
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-center p-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CreditCard className="h-5 w-5" />
          Subscription Overview
        </CardTitle>
        <CardDescription>
          {filteredSubs.length} total users • {filteredSubs.filter(s => s.subscription_status === 'active').length} active subscriptions
        </CardDescription>
        <div className="flex gap-2 mt-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <Button onClick={fetchSubscriptionData} variant="outline">
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {filteredSubs.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              No users found
            </p>
          ) : (
            filteredSubs.map((sub) => (
              <div
                key={sub.user_id}
                className="flex flex-col lg:flex-row lg:items-center justify-between p-4 border rounded-lg hover:bg-accent/50 transition-colors gap-4"
              >
                <div className="space-y-2 flex-1">
                  <div className="flex items-start gap-3 flex-wrap">
                    <div>
                      <div className="font-medium flex items-center gap-2">
                        <User className="h-4 w-4 text-muted-foreground" />
                        {sub.display_name}
                      </div>
                      <div className="text-sm text-muted-foreground flex items-center gap-2 mt-1">
                        <Mail className="h-3 w-3" />
                        {sub.email}
                      </div>
                    </div>
                    {getStatusBadge(sub.subscription_status, sub.is_trial_active)}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                    {sub.subscription_status === 'active' && (
                      <>
                        {sub.price_id && (
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <CreditCard className="h-4 w-4" />
                            Price: {sub.price_id}
                          </div>
                        )}
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Calendar className="h-4 w-4" />
                          Renews: {sub.current_period_end ? format(new Date(sub.current_period_end), 'PP') : 'N/A'}
                        </div>
                      </>
                    )}
                    {sub.is_trial_active && sub.trial_end_date && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Calendar className="h-4 w-4" />
                        Trial ends: {format(new Date(sub.trial_end_date), 'PP')}
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="text-xs">
                        {sub.monthly_credits} monthly
                      </Badge>
                      {sub.purchased_credits > 0 && (
                        <Badge variant="outline" className="text-xs">
                          {sub.purchased_credits} purchased
                        </Badge>
                      )}
                    </div>
                  </div>

                  {sub.stripe_customer_id && (
                    <div className="text-xs text-muted-foreground">
                      Customer ID: {sub.stripe_customer_id}
                    </div>
                  )}
                </div>

                {sub.stripe_customer_id && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.open(`https://dashboard.stripe.com/customers/${sub.stripe_customer_id}`, '_blank')}
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    View in Stripe
                  </Button>
                )}
              </div>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default SubscriptionOverview;
